/***
*
*   AUTHENTICATION
*   Auth provider to manage auth functions throughout
*   the application. <PrivateRoute> component to
*   protect internal application routes from unauthenticated
*   access.
*
**********/

import React, { useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { clientConfig, defaultRoute, entraAuth } from './config';

// auth context
export const AuthContext = React.createContext();

const permissions = require('./permissions.json');

// CORE-4859: in entra mode a 401 means the ALB session or its token has
// lapsed. Sign in with Microsoft again as a full page load, at most once a
// minute: a second 401 inside the window means that did not fix it, so the
// caller shows an error instead of looping. Used by the signin view and by
// the View's 401 handler (useAPI, Form, modals).
const REAUTH_KEY = 'mc_reauth_at';
const REAUTH_WINDOW_MS = 60 * 1000;

// set by signout: a request the browser drops on the way out must not turn
// the sign-out into a re-auth
let signingOut = false;

export function reauthenticate(){

  if (signingOut)
    return true;

  const last = Number(sessionStorage.getItem(REAUTH_KEY));
  if (last && Date.now() - last < REAUTH_WINDOW_MS)
    return false;

  sessionStorage.setItem(REAUTH_KEY, String(Date.now()));
  localStorage.removeItem('user'); // /signin asks /api/me again on return
  window.location.assign('/api/auth/logout?reauth=1');
  return true;

}

// CORE-4859: once the ALB session expires it answers a Mission Control /api
// call with a redirect to Microsoft, which the browser refuses cross-origin,
// so Axios sees a network error with no response. Treated like a 401.
export function sessionLost(err){

  return !!err && !err.response && err.code === 'ERR_NETWORK' &&
    /^\/api\//.test(err.config?.url || '');

}

export function AuthProvider(props){

  const cache = JSON.parse(localStorage.getItem('user'));
  const [user, setUser] = useState(cache);

  function signin(res, impersonate){

    // CORE-4859: the ALB session cookie is the credential, so keep the
    // /api/me profile only: no token, no Authorization header
    if (entraAuth()){

      if (res.data){

        localStorage.setItem('user', JSON.stringify(res.data));
        window.location = defaultRoute();

      }

      return;

    }

    if (res.data){

      localStorage.setItem('user', JSON.stringify(res.data));
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + res.data.token;

      if (res.data.permission !== 'master')
        return window.location = '/signin';
      
      return window.location = impersonate ?
        `${clientConfig().remote_client}/support` : defaultRoute();

    }
  }

  async function signout(){

    // CORE-4859: ends the ALB session, then Entra's
    if (entraAuth()){

      signingOut = true;
      localStorage.clear();
      window.location = '/api/auth/logout';
      return;

    }

    axios({ method: 'delete', url: `${clientConfig().server_url}/api/auth` });
    localStorage.clear();
    window.location = '/signin'

  }

  function update(data){

    if (localStorage.getItem('user')){

      let user = JSON.parse(localStorage.getItem('user'));
      for (let key in data){

        if (Array.isArray(data[key])){
       
          user[key] = data[key]

        }
        else if (typeof data[key] === 'object'){
          for (let innerKey in data[key]){

            user[key][innerKey] = data[key][innerKey]

          }
        }
        else {
          
          user[key] = data[key];

        }
      }

      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

    }
  }

  return (
    <AuthContext.Provider value={{

      user: user,
      signin: signin,
      signout: signout,
      update: update,
      remote_server: clientConfig().remote_server,
      permission: permissions[user?.permission]

    }}

    {...props} />
  );
}

// custom route object checks for an auth token before
// rendering the route – redirects if token is not present
export function PrivateRoute(props){

  // check user
  const user = JSON.parse(localStorage.getItem('user'));

  // CORE-4859: no token in entra mode, the permission came from /api/me
  if (entraAuth()){

    if (user?.permission && permissions[user.permission]?.[props.permission])
      return props.children;

    return <Navigate to='/signin' />;

  }

  if (user?.token && permissions[user.permission][props.permission])
    return props.children;

  // user is not authenticated
  return <Navigate to='/signin' />;

}
